import React, { useState, useEffect, useContext } from 'react';
import api, { resolveUrl } from '../utils/api';
import { getTheme } from '../utils/theme';
import { NotificationContext } from '../components/Layout';

const ReportIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>;

const theme = getTheme();

function AvatarLarge({ url, name, isOnline, onClick }) {
  const size = 100;
  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto', cursor: onClick && url ? 'zoom-in' : 'default' }} onClick={onClick}>
      {url ? (
        <img src={url} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `3px solid ${theme.colors.primary}` }} onError={e => e.target.style.display='none'} />
      ) : (
        <div style={{ width: size, height: size, borderRadius: '50%', background: theme.colors.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#fff', fontSize: size * 0.38, border: `3px solid ${theme.colors.primaryHover}` }}>
          {name?.[0]?.toUpperCase() || '?'}
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 4, right: 4, width: 18, height: 18, borderRadius: '50%', border: '3px solid #16161d', background: isOnline ? '#22c55e' : '#475569' }} />
    </div>
  );
}

export default function UserProfileModal({ userId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const { showNotification } = useContext(NotificationContext) || {};

  const reportUser = async () => {
    const reason = window.prompt(`Reason for reporting ${profile.username}:`);
    if (reason === null) return;
    try {
      await api.post('/report/', { reported_user_id: profile.id, reason: reason || 'Inappropriate behavior' });
      showNotification?.('User reported to admins.');
    } catch {}
  };

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/user-profile/${userId}/`)
      .then(res => setProfile(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [userId]);

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        {lightbox && (
          <div style={S.lightboxOverlay} onClick={() => setLightbox(null)}>
            <img src={lightbox} alt="full" style={S.lightboxImg} onClick={e => e.stopPropagation()} />
            <button style={S.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
          </div>
        )}
        <button style={S.closeBtn} onClick={onClose}>✕</button>
        
        {loading ? (
          <div style={S.loader}>Loading profile...</div>
        ) : !profile ? (
          <div style={S.loader}>User not found.</div>
        ) : (
          <div style={S.content}>
            <AvatarLarge url={resolveUrl(profile.avatar)} name={profile.username} isOnline={profile.is_online} onClick={() => { if(profile.avatar) setLightbox(resolveUrl(profile.avatar)); }} />
            <h2 style={S.name}>{profile.username}</h2>
            <div style={S.meta}>
              {profile.gender && <span>{profile.gender}</span>}
              {profile.gender && profile.country && <span> • </span>}
              {profile.country && <span>{profile.country}</span>}
            </div>
            
            {(profile.gender || profile.country) && <hr style={S.divider} />}
            
            <div style={S.bioBox}>
              <h3 style={S.bioTitle}>About</h3>
              <p style={S.bioText}>{profile.bio || "This user hasn't written a bio yet."}</p>
            </div>
            
            <div style={S.lastSeen}>
              {profile.is_online ? 'Currently Online' : `Last seen: ${profile.last_seen ? new Date(profile.last_seen).toLocaleString() : 'Unknown'}`}
            </div>
            
            <button style={S.reportBtn} onClick={reportUser}>
              <ReportIcon /> Report User
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
    zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(4px)', padding: 16
  },
  modal: {
    width: '100%', maxWidth: 360, background: '#16161d', borderRadius: 24,
    border: `1px solid rgba(${theme.colors.primaryRGB},0.2)`,
    boxShadow: '0 20px 40px rgba(0,0,0,0.5)', overflow: 'hidden', position: 'relative',
    animation: 'toastIn 0.3s ease-out'
  },
  closeBtn: {
    position: 'absolute', top: 12, right: 12, width: 32, height: 32,
    borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: 'none',
    color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 10
  },
  loader: { padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  content: { padding: '32px 24px' },
  name: { textAlign: 'center', margin: '16px 0 4px', fontSize: 20, fontWeight: 700, color: '#e2e8f0' },
  meta: { textAlign: 'center', color: '#94a3b8', fontSize: 12, textTransform: 'capitalize' },
  divider: { border: 'none', borderTop: '1px solid #2d2d3d', margin: '20px 0' },
  bioBox: { background: '#111118', borderRadius: 12, padding: 16, border: '1px solid #2d2d3d' },
  bioTitle: { fontSize: 11, fontWeight: 600, color: theme.colors.primary, textTransform: 'uppercase', marginBottom: 8, letterSpacing: 1 },
  bioText: { fontSize: 13, color: '#e2e8f0', lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap' },
  lastSeen: { textAlign: 'center', marginTop: 24, fontSize: 11, color: '#64748b' },
  lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lightboxImg: { maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 },
  lightboxClose: { position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: '#fff', fontSize: 16, cursor: 'pointer', zIndex: 100001 },
  reportBtn: { marginTop: 24, width: '100%', padding: '10px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 600 }
};
